import { useMemo } from "react";
import { useAllExperiences } from "../../../hooks/useAllExperiences";
import { useExternalExperiences } from "../../../hooks/useExternalExperiences";
import { UserGroupExperienceRow } from "./useUserGroupExperiences";

interface UseUserGroupExternalExperiencesOptions {
  userGroupId?: string;
  enabled?: boolean;
}

interface UseUserGroupExternalExperiencesResult {
  experiences: UserGroupExperienceRow[];
  isLoading: boolean;
}

export const useUserGroupExternalExperiences = (
  options: UseUserGroupExternalExperiencesOptions,
): UseUserGroupExternalExperiencesResult => {
  const { userGroupId } = options;

  const { experiencesMap, isLoading: isAllExperiencesLoading } =
    useAllExperiences();
  const { groupIdToAllocationMap, isLoading: isExternalExperiencesLoading } =
    useExternalExperiences();

  const experiences = useMemo(() => {
    if (!userGroupId) return [];

    const allocations = groupIdToAllocationMap.get(userGroupId) ?? [];

    return allocations
      .map(({ allocation, license }) => {
        const experience = experiencesMap.get(license.releaseChannelId);
        if (!experience) return undefined;

        const row: UserGroupExperienceRow = {
          id: license.releaseChannelId,
          title: experience.friendlyName,
          companyName: experience.companyName,
          expirationDate: allocation.expiresAt,
          contentMetadataId: experience.contentMetadataId,
        };

        return row;
      })
      .filter((item): item is UserGroupExperienceRow => item !== undefined);
  }, [userGroupId, groupIdToAllocationMap, experiencesMap]);

  const isLoading = isAllExperiencesLoading || isExternalExperiencesLoading;

  return useMemo(
    () => ({
      experiences,
      isLoading,
    }),
    [experiences, isLoading],
  );
};
